import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Eye, Grid2X2, PauseCircle, Pencil, Play, PlayCircle, Plus, RotateCcw, Search, SlidersHorizontal, StopCircle, Table2, XCircle, ClipboardList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { IconButton } from '@/Components/ui/IconButton';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { CountBadge, StatusBadge } from '@/Components/ui/Badge';
import { FilterBar, FilterField } from '@/Components/data/FilterBar';
import { SearchField } from '@/Components/forms/SearchField';
import type { Tone } from '@/Components/ui/types';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import { DataTableColumnsMenu } from '@/Components/data/DataTableColumnsMenu';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/Components/ui/DropdownMenu';
import ordemProducaoService, {
  type OrdemProducao,
  type OrdemProducaoStatus,
} from '@/services/ordemProducao';

const STATUS_LABELS: Record<OrdemProducaoStatus, string> = {
  ABERTA: 'Aberta',
  LIBERADA: 'Liberada',
  EM_PRODUCAO: 'Em Produção',
  PAUSADA: 'Pausada',
  ENCERRADA: 'Encerrada',
  CANCELADA: 'Cancelada',
};
const STATUS_TONE: Record<OrdemProducaoStatus, Tone> = {
  ABERTA: 'neutral',
  LIBERADA: 'info',
  EM_PRODUCAO: 'warning',
  PAUSADA: 'purple',
  ENCERRADA: 'success',
  CANCELADA: 'danger',
};

type OrdemColumnId = 'numero' | 'produto' | 'status' | 'quantidade' | 'inicio';

const ordemColumns: { id: OrdemColumnId; label: string }[] = [
  { id: 'numero', label: 'Número' },
  { id: 'produto', label: 'Produto' },
  { id: 'status', label: 'Status' },
  { id: 'quantidade', label: 'Quantidade' },
  { id: 'inicio', label: 'Início Planejado' },
];

function formatNumber(v: number | string | null | undefined) {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function formatDate(v?: string | null) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('pt-BR');
}

function renderOrdemColumn(item: OrdemProducao, columnId: OrdemColumnId): ReactNode {
  if (columnId === 'numero') return item.numero;
  if (columnId === 'produto') return (
    <>
      <div>{item.produto_nome}</div>
      <span style={{ fontSize: '0.75rem', color: 'var(--nx-muted)' }}>{item.produto_codigo}</span>
    </>
  );
  if (columnId === 'status') return <StatusBadge label={STATUS_LABELS[item.status]} tone={STATUS_TONE[item.status]} />;
  if (columnId === 'inicio') return formatDate(item.data_inicio_planejada);

  const percentual = item.quantidade_planejada
    ? Math.min(100, (Number(item.quantidade_produzida) / Number(item.quantidade_planejada)) * 100)
    : 0;
  return (
    <>
      <div>{formatNumber(item.quantidade_planejada)}</div>
      <div style={{ marginTop: 4, height: 4, width: 112, borderRadius: 9999, background: 'var(--nx-border)' }}>
        <div style={{ height: 4, borderRadius: 9999, width: `${percentual}%`, background: 'var(--nx-primary)' }} />
      </div>
      <span style={{ fontSize: '0.75rem', color: 'var(--nx-muted)' }}>{formatNumber(item.quantidade_produzida)} produzido</span>
    </>
  );
}

export default function OrdemProducaoPage() {
  const navigate = useNavigate();
  const [ordens, setOrdens] = useState<OrdemProducao[]>([]);
  const [loading, setLoading] = useState(false);
  const [processando, setProcessando] = useState(false);

  const [busca, setBusca] = useState('');
  const [draftBusca, setDraftBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [draftFiltroStatus, setDraftFiltroStatus] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [draftFiltroDataInicio, setDraftFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [draftFiltroDataFim, setDraftFiltroDataFim] = useState('');
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [compactActions, setCompactActions] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Record<OrdemColumnId, boolean>>(
    () => Object.fromEntries(ordemColumns.map((column) => [column.id, true])) as Record<OrdemColumnId, boolean>,
  );
  const [columnOrder, setColumnOrder] = useState<OrdemColumnId[]>(() => ordemColumns.map((column) => column.id));

  const [dialogIniciar, setDialogIniciar] = useState(false);
  const [opIniciar, setOpIniciar] = useState<OrdemProducao | null>(null);
  const [dataInicioReal, setDataInicioReal] = useState('');

  const [dialogEncerrar, setDialogEncerrar] = useState(false);
  const [opEncerrar, setOpEncerrar] = useState<OrdemProducao | null>(null);
  const [dataFimReal, setDataFimReal] = useState('');
  const [quantidadeFinal, setQuantidadeFinal] = useState(0);

  const [dialogCancelar, setDialogCancelar] = useState(false);
  const [opCancelar, setOpCancelar] = useState<OrdemProducao | null>(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');

  const carregar = async (filters = { status: filtroStatus, search: busca }) => {
    setLoading(true);
    try {
      const data = await ordemProducaoService.listar({
        status: filters.status || undefined,
        search: filters.search || undefined,
      });
      setOrdens(data);
    } catch (error) {
      console.error('Erro ao carregar ordens de produção', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus, busca]);

  const ordensFiltradas = useMemo(() => {
    let lista = ordens;
    if (busca) {
      const termo = busca.toLowerCase();
      lista = lista.filter(
        (o) =>
          o.numero.toLowerCase().includes(termo) ||
          (o.produto_nome || '').toLowerCase().includes(termo) ||
          (o.lote_producao || '').toLowerCase().includes(termo) ||
          (STATUS_LABELS[o.status] || o.status).toLowerCase().includes(termo)
      );
    }
    if (filtroDataInicio) {
      lista = lista.filter((o) => o.data_inicio_planejada && o.data_inicio_planejada.slice(0, 10) >= filtroDataInicio);
    }
    if (filtroDataFim) {
      lista = lista.filter((o) => o.data_inicio_planejada && o.data_inicio_planejada.slice(0, 10) <= filtroDataFim);
    }
    return lista;
  }, [ordens, busca, filtroDataInicio, filtroDataFim]);
  const lastPage = Math.max(1, Math.ceil(ordensFiltradas.length / perPage));
  const currentPage = Math.min(page, lastPage);
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return ordensFiltradas.slice(start, start + perPage);
  }, [currentPage, ordensFiltradas, perPage]);
  const activeColumns = useMemo(
    () => columnOrder
      .map((id) => ordemColumns.find((column) => column.id === id))
      .filter((column): column is (typeof ordemColumns)[number] => Boolean(column && visibleColumns[column.id])),
    [columnOrder, visibleColumns],
  );
  const from = ordensFiltradas.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const to = ordensFiltradas.length === 0 ? 0 : Math.min(from + pageData.length - 1, ordensFiltradas.length);

  const submitFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(1);
    setBusca(draftBusca.trim());
    setFiltroStatus(draftFiltroStatus);
    setFiltroDataInicio(draftFiltroDataInicio);
    setFiltroDataFim(draftFiltroDataFim);
  };

  const clearFilters = () => {
    setDraftBusca('');
    setBusca('');
    setDraftFiltroStatus('');
    setFiltroStatus('');
    setDraftFiltroDataInicio('');
    setFiltroDataInicio('');
    setDraftFiltroDataFim('');
    setFiltroDataFim('');
    setPage(1);
  };

  const liberarOP = async (item: OrdemProducao) => {
    setProcessando(true);
    try {
      await ordemProducaoService.liberar(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao liberar OP', error);
    } finally {
      setProcessando(false);
    }
  };

  const abrirIniciar = (item: OrdemProducao) => {
    setOpIniciar(item);
    setDataInicioReal(new Date().toISOString().slice(0, 16));
    setDialogIniciar(true);
  };

  const confirmarIniciar = async () => {
    if (!opIniciar || !dataInicioReal) return;
    setProcessando(true);
    try {
      await ordemProducaoService.iniciar(opIniciar.id, dataInicioReal);
      setDialogIniciar(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao iniciar OP', error);
    } finally {
      setProcessando(false);
    }
  };

  const pausarOP = async (item: OrdemProducao) => {
    setProcessando(true);
    try {
      await ordemProducaoService.pausar(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao pausar OP', error);
    } finally {
      setProcessando(false);
    }
  };

  const abrirEncerrar = (item: OrdemProducao) => {
    setOpEncerrar(item);
    setDataFimReal(new Date().toISOString().slice(0, 16));
    setQuantidadeFinal(Number(item.quantidade_produzida) || Number(item.quantidade_planejada) || 0);
    setDialogEncerrar(true);
  };

  const confirmarEncerrar = async () => {
    if (!opEncerrar || !dataFimReal || !quantidadeFinal) return;
    setProcessando(true);
    try {
      await ordemProducaoService.encerrar(opEncerrar.id, dataFimReal, Number(quantidadeFinal));
      setDialogEncerrar(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao encerrar OP', error);
    } finally {
      setProcessando(false);
    }
  };

  const abrirCancelar = (item: OrdemProducao) => {
    setOpCancelar(item);
    setMotivoCancelamento('');
    setDialogCancelar(true);
  };

  const confirmarCancelar = async () => {
    if (!opCancelar) return;
    setProcessando(true);
    try {
      await ordemProducaoService.cancelar(opCancelar.id, motivoCancelamento);
      setDialogCancelar(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao cancelar OP', error);
    } finally {
      setProcessando(false);
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<ClipboardList size={21} strokeWidth={2} />}
        title="Ordens de Produção"
        description="Controle de PCP: liberação, execução e encerramento das ordens de produção."
        actions={
          <Button type="button" onClick={() => navigate('/producao/ordens/nova')}>
            <Plus size={16} strokeWidth={2.2} />
            Nova OP
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions nx-listing-toolbar">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="ghost">
                    <Grid2X2 size={16} />
                    Visualização
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Tipo de visualização</DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={viewMode} onValueChange={(value) => setViewMode(value as 'table' | 'grid')}>
                    <DropdownMenuRadioItem value="table"><Table2 size={15} /> Tabela</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="grid"><Grid2X2 size={15} /> Grade</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <DataTableColumnsMenu
                options={ordemColumns}
                columnOrder={columnOrder}
                visibility={visibleColumns}
                onToggle={(id, visible) => setVisibleColumns((current) => ({ ...current, [id]: visible }))}
                onReorder={(order) => setColumnOrder([...order])}
                onReset={() => {
                  setVisibleColumns(Object.fromEntries(ordemColumns.map((column) => [column.id, true])) as Record<OrdemColumnId, boolean>);
                  setColumnOrder(ordemColumns.map((column) => column.id));
                }}
              />
              <Button type="button" variant="ghost" aria-pressed={compactActions} onClick={() => setCompactActions((value) => !value)}>
                <SlidersHorizontal size={15} />
                Modo de Ações
              </Button>
              <CountBadge count={`${ordensFiltradas.length} registros`} />
            </div>
          }
        />

        <FilterBar
          onSubmit={submitFilters}
          search={
            <SearchField
              label="Buscar por número, produto ou lote"
              placeholder="Buscar número, produto ou lote..."
              value={draftBusca}
              onChange={(event) => setDraftBusca(event.target.value)}
            />
          }
          filters={
            <>
              <FilterField label="Status" className="nx-sngpc-filter-field">
                <Select filter value={draftFiltroStatus} onChange={(event) => setDraftFiltroStatus(event.target.value)}>
                  <option value="">Todos</option>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
              </FilterField>
              <FilterField label="Início de">
                <Input type="date" value={draftFiltroDataInicio} onChange={(event) => setDraftFiltroDataInicio(event.target.value)} />
              </FilterField>
              <FilterField label="Até">
                <Input type="date" value={draftFiltroDataFim} onChange={(event) => setDraftFiltroDataFim(event.target.value)} />
              </FilterField>
              <FilterField label="Por página">
                <Select filter value={perPage} onChange={(event) => { setPage(1); setPerPage(Number(event.target.value)); }}>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Select>
              </FilterField>
            </>
          }
          actions={<Button type="button" variant="ghost" onClick={clearFilters}><RotateCcw size={15} /> Limpar</Button>}
        />

        {!loading && ordensFiltradas.length === 0 ? (
          <EmptyState message="Nenhuma ordem de produção encontrada." />
        ) : viewMode === 'grid' ? (
          <div className="nx-grid-3">
            {pageData.map((item) => (
              <Card key={item.id} className="nx-sngpc-item-card">
                <div className="nx-sngpc-item-card__header">
                  <div>
                    <strong>{item.numero}</strong>
                    <span>{item.produto_nome || '-'}</span>
                  </div>
                  <StatusBadge label={STATUS_LABELS[item.status]} tone={STATUS_TONE[item.status]} />
                </div>
                <div className="nx-sngpc-item-card__body">
                  <span>{formatNumber(item.quantidade_planejada)} planejado</span>
                  <span>{formatNumber(item.quantidade_produzida)} produzido</span>
                  <span>{formatDate(item.data_inicio_planejada)}</span>
                </div>
                <div className="nx-table-actions-group nx-table-actions-group--buttons">
                  <IconButton label="Detalhes" onClick={() => navigate(`/producao/ordens/${item.id}/visualizar`)}><Eye size={15} strokeWidth={1.9} aria-hidden /></IconButton>
                  <IconButton label="Editar" onClick={() => navigate(`/producao/ordens/${item.id}/editar`)}><Pencil size={15} strokeWidth={1.9} aria-hidden /></IconButton>
                  <IconButton label="Liberar" variant="primary" onClick={() => liberarOP(item)} disabled={item.status !== 'ABERTA' || processando}><PlayCircle size={15} strokeWidth={1.9} aria-hidden /></IconButton>
                  <IconButton label="Cancelar" variant="danger" onClick={() => abrirCancelar(item)} disabled={['ENCERRADA', 'CANCELADA'].includes(item.status)}><XCircle size={15} strokeWidth={1.9} aria-hidden /></IconButton>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  {activeColumns.map((column) => <th key={column.id}>{column.label}</th>)}
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={activeColumns.length + 1} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  pageData.map((item) => (
                      <tr key={item.id}>
                        {activeColumns.map((column) => <td key={column.id}>{renderOrdemColumn(item, column.id)}</td>)}
                        <td className="nx-col-acoes">
                          <div className="nx-table-actions-group nx-table-actions-group--buttons">
                            <IconButton label="Detalhes" onClick={() => navigate(`/producao/ordens/${item.id}/visualizar`)}>
                              <Eye size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                            <IconButton label="Editar" onClick={() => navigate(`/producao/ordens/${item.id}/editar`)}>
                              <Pencil size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                            <IconButton
                              label="Liberar"
                              variant="primary"
                              onClick={() => liberarOP(item)}
                              disabled={item.status !== 'ABERTA' || processando}
                            >
                              <PlayCircle size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                            <IconButton
                              label="Iniciar"
                              variant="primary"
                              onClick={() => abrirIniciar(item)}
                              disabled={item.status !== 'LIBERADA'}
                            >
                              <Play size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                            <IconButton
                              label="Pausar"
                              onClick={() => pausarOP(item)}
                              disabled={item.status !== 'EM_PRODUCAO' || processando}
                            >
                              <PauseCircle size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                            <IconButton
                              label="Encerrar"
                              onClick={() => abrirEncerrar(item)}
                              disabled={!['EM_PRODUCAO', 'PAUSADA'].includes(item.status)}
                            >
                              <StopCircle size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                            <IconButton
                              label="Cancelar"
                              variant="danger"
                              onClick={() => abrirCancelar(item)}
                              disabled={['ENCERRADA', 'CANCELADA'].includes(item.status)}
                            >
                              <XCircle size={15} strokeWidth={1.9} aria-hidden />
                            </IconButton>
                          </div>
                        </td>
                      </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="nx-table-footer">
          <span>{ordensFiltradas.length === 0 ? '0 registros' : `${from}-${to} de ${ordensFiltradas.length} registros`}</span>
          <div className="nx-table-pagination">
            <Button type="button" variant="ghost" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={currentPage <= 1}>Anterior</Button>
            <span>{currentPage} / {lastPage}</span>
            <Button type="button" variant="ghost" onClick={() => setPage((value) => Math.min(lastPage, value + 1))} disabled={currentPage >= lastPage}>Próxima</Button>
          </div>
        </div>
      </Card>

      {/* Dialog Iniciar */}
      <Dialog open={dialogIniciar} onOpenChange={(next) => !processando && setDialogIniciar(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Iniciar Produção</DialogTitle>
            <DialogDescription>Informe a data/hora real de início da produção da OP {opIniciar?.numero ?? ''}.</DialogDescription>
          </DialogHeader>

          <Field label="Data/Hora de Início Real" htmlFor="op-inicio-real">
            <Input
              id="op-inicio-real"
              type="datetime-local"
              value={dataInicioReal}
              onChange={(e) => setDataInicioReal(e.target.value)}
            />
          </Field>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={processando}>Cancelar</Button>
            </DialogClose>
            <Button type="button" loading={processando} loadingText="Iniciando..." onClick={confirmarIniciar}>
              Iniciar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Encerrar */}
      <Dialog open={dialogEncerrar} onOpenChange={(next) => !processando && setDialogEncerrar(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Encerrar Ordem de Produção</DialogTitle>
            <DialogDescription>Informe os dados reais de encerramento da OP {opEncerrar?.numero ?? ''}.</DialogDescription>
          </DialogHeader>

          <div className="nx-form-grid">
            <Field label="Data/Hora de Fim Real" htmlFor="op-fim-real" className="nx-sngpc-field--full">
              <Input
                id="op-fim-real"
                type="datetime-local"
                value={dataFimReal}
                onChange={(e) => setDataFimReal(e.target.value)}
              />
            </Field>
            <Field label="Quantidade Produzida Final" htmlFor="op-quantidade-final" className="nx-sngpc-field--full">
              <DecimalInput
                id="op-quantidade-final"
                value={quantidadeFinal}
                onValueChange={(value) => setQuantidadeFinal(value ?? 0)}
              />
            </Field>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={processando}>Cancelar</Button>
            </DialogClose>
            <Button type="button" loading={processando} loadingText="Encerrando..." onClick={confirmarEncerrar}>
              Encerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Cancelar */}
      <Dialog open={dialogCancelar} onOpenChange={(next) => !processando && setDialogCancelar(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Ordem de Produção</DialogTitle>
            <DialogDescription>Informe o motivo do cancelamento da OP {opCancelar?.numero ?? ''}.</DialogDescription>
          </DialogHeader>

          <Field label="Motivo do cancelamento" htmlFor="op-motivo-cancelamento">
            <Textarea
              id="op-motivo-cancelamento"
              value={motivoCancelamento}
              onChange={(e) => setMotivoCancelamento(e.target.value)}
              rows={3}
            />
          </Field>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={processando}>Voltar</Button>
            </DialogClose>
            <Button type="button" variant="danger" loading={processando} loadingText="Cancelando..." onClick={confirmarCancelar}>
              Confirmar Cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
